import { useState } from 'react'
import { callFn } from '../../lib/supabase'
import { useMeta } from '../ReportsApp'
import { dropReportCache } from '../api'
import { money, n } from '../format'
import { Button, Field, OwnerId } from '../ui'

export interface PayeeTarget { payee: string; name: string; consignor_key: string | null; is_masih: boolean; consignify: number; bank: number; crypto: number; handle?: string | null; preferred_method?: string | null }
export interface BoughtTarget { intake_id: string; intake_no: number; date: string; from_name: string; piece: string; cost: number; method: string | null; whose: string | null }

const METHODS: Record<string, { v: string; label: string }[]> = {
  consignify: [{ v: 'consignify', label: 'Consignify payout' }],
  bank: [{ v: 'zelle', label: 'Zelle' }, { v: 'wire', label: 'Wire' }, { v: 'cash', label: 'Cash' }, { v: 'check', label: 'Check' }],
  crypto: [{ v: 'crypto', label: 'Crypto transfer' }],
  cash: [{ v: 'cash', label: 'Cash' }],
}
const COINS = ['USDC', 'USDT', 'BTC', 'ETH', 'SOL']

/** R3b: record a payout to a consignor or to Masih on one channel, or for a bought piece. Signed with an owner ID; calls payout-record. */
export function PayoutForm({ payee, bought, onDone }: { payee?: PayeeTarget; bought?: BoughtTarget; onDone: () => void }) {
  const { meta } = useMeta()
  const owners = meta?.owners ?? []
  const first = payee ? (['consignify', 'bank', 'crypto'] as const).find((c) => n(payee[c]) > 0) ?? 'bank' : (bought?.method === 'wire' || bought?.method === 'zelle' || bought?.method === 'check' ? 'bank' : bought?.method === 'crypto' ? 'crypto' : bought?.method === 'cash' ? 'cash' : 'bank')
  const [channel, setChannel] = useState<string>(first)
  const [method, setMethod] = useState<string>(bought?.method && METHODS[first]?.some((m) => m.v === bought.method) ? bought.method : METHODS[first][0].v)
  const [coin, setCoin] = useState('USDC')
  const [amount, setAmount] = useState<string>(() => payee ? n(payee[first as 'bank']).toFixed(2) : bought ? n(bought.cost).toFixed(2) : '')
  const [reference, setReference] = useState('')
  const [proof, setProof] = useState('')
  const [note, setNote] = useState('')
  const [owner, setOwner] = useState('')
  const [override, setOverride] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  // picking a channel prefills the amount owed on it and its first method
  const pickChannel = (c: string) => {
    setChannel(c)
    if (payee) { setAmount(n(payee[c as 'bank']).toFixed(2)); setMethod(METHODS[c][0].v) }
    else setMethod((cur) => METHODS[c].some((m) => m.v === cur) ? cur : METHODS[c][0].v)
  }
  const cents = Math.round(Number(amount || 0) * 100)
  const owed = payee ? Math.round(n(payee[channel as 'bank']) * 100) : bought ? Math.round(n(bought.cost) * 100) : 0
  const over = payee && cents > owed + 100
  const ok = cents > 0 && owner.length === 4 && (reference.trim() || proof.trim()) && (!over || override)
  const submit = async () => {
    setBusy(true); setMsg('')
    try {
      const body: Record<string, unknown> = { employee_id: owner, channel, method, amount_cents: cents, reference: reference.trim(), proof_url: proof.trim(), note: note.trim(), coin: channel === 'crypto' ? coin : null, override }
      if (payee) { body.kind = payee.is_masih && payee.payee === 'partner:masih' ? 'partner' : 'consignor'; body.payee = payee.payee; body.consignor_key = payee.consignor_key }
      else if (bought) { body.kind = 'bought'; body.intake_id = bought.intake_id }
      await callFn('payout-record', body)
      dropReportCache()
      setMsg('Recorded'); onDone()
    } catch (e) { setMsg((e as Error).message) } finally { setBusy(false) }
  }
  return (
    <div className="rp-form">
      {payee && (
        <Field label="Channel" hint="Pays the money owed on that channel only; the other channels stay owed">
          <div className="rp-choice">
            {(['consignify', 'bank', 'crypto'] as const).map((c) => (
              <button key={c} type="button" aria-pressed={channel === c} onClick={() => pickChannel(c)}>
                {c === 'consignify' ? 'Consignify' : c === 'bank' ? 'Bank' : 'Crypto'}
                <small>{n(payee[c]) > 0 ? money(payee[c]) : 'nothing owed'}</small>
              </button>
            ))}
          </div>
        </Field>
      )}
      <div className="rp-form__row">
        <Field label="Method">
          <select className="rp-select" value={method} onChange={(e) => setMethod(e.target.value)}>
            {(payee ? METHODS[channel] : [...METHODS.bank, ...METHODS.crypto]).map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
          </select>
        </Field>
        <Field label="Amount">
          <input className="rp-input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))} />
        </Field>
      </div>
      {(channel === 'crypto' || method === 'crypto') && (
        <Field label="Coin"><select className="rp-select" value={coin} onChange={(e) => setCoin(e.target.value)}>{COINS.map((c) => <option key={c}>{c}</option>)}</select></Field>
      )}
      {payee?.handle && <p className="rp-field__hint">On file for {payee.name}: {payee.handle}{payee.preferred_method ? ` (${payee.preferred_method})` : ''}</p>}
      <Field label="Reference (Zelle confirmation, wire reference, transaction hash or Consignify payout number)">
        <input className="rp-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. Zelle CHASE-8F2K9Q" />
      </Field>
      <Field label="Proof link" hint="A link to the bank confirmation, the Consignify payout or the transaction on the chain; a reference or a proof is required">
        <input className="rp-input" value={proof} onChange={(e) => setProof(e.target.value)} placeholder="https://…" />
      </Field>
      <Field label="Note (optional)">
        <input className="rp-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder={payee ? 'Paid the bank-side balance; Consignify side next week' : ''} />
      </Field>
      {over && (
        <label className="rp-access__remember" style={{ color: '#7a4f00' }}>
          <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} style={{ accentColor: '#7a4f00' }} />
          {money(cents / 100)} is more than the {money(owed / 100)} owed on this channel. Record it anyway (the note says why).
        </label>
      )}
      <OwnerId value={owner} onChange={setOwner} owners={owners} />
      <p className="rp-field__hint">Writes the payout, clears this channel's balance, and lands in Polaris at the next sync. The money itself moves outside the app.</p>
      <Button primary disabled={!ok || busy} onClick={submit}>{busy ? 'Recording…' : `Record ${money(cents / 100)}`}</Button>
      {msg && <div className={msg === 'Recorded' ? 'rp-tone-green' : 'rp-error'} style={{ fontSize: 14 }}>{msg}</div>}
    </div>
  )
}
